/**
 * SESSION STATUS BADGE - REDISEÑO PRO V4
 * =======================================
 * 🎟️ TICKET P0: Rediseño PRO de Tarjeta "Estado de Sesión" V4 (sin redundancia con banner)
 * 
 * Componente PRO que muestra el estado del Motor V4 sin redundancias:
 * - Estado de sesión (orientación/progreso, NO warnings/sanctions)
 * - Fase actual con objetivos pedagógicos
 * - Progreso de fase y progreso total
 * - Diseño neutral, pedagógico y profesional
 * 
 * REGLAS DE DISEÑO PRO:
 * 1. La tarjeta NO cambia color de fondo por estado (solo #F9FAFB)
 * 2. Estado se expresa con icono + texto (sin saturación fuerte)
 * 3. ELIMINAR warnings/errores (banner es el único lugar para eso)
 * 4. Foco en orientación/progreso, NO en sanción/explicación
 * 
 * Props:
 * - dataSource: { sessionStatusAfter, sessionStatusBefore, status, aiTurnCount }
 * - moderatorAlert: Solo para detectar pausa (NO mostrar detalles)
 * - currentPhase: Fase actual de la state machine
 * - currentPhaseDisplayName: Nombre visual de la fase
 * - turnsInPhase: Turnos completados en la fase actual
 * - maxTurns: Límite máximo de turnos
 * - completedTurns: Turnos completados totales (fuente de verdad V4)
 */

import React from 'react';
import { 
  Play, 
  Pause, 
  CheckCircle, 
  Clock
} from 'lucide-react';

/**
 * 🎟️ V4-P0-008: Helper para resolver estado de sesión con prioridad estricta
 * REGLA: sessionStatusAfter > sessionStatusBefore > status > "unknown"
 */
function resolveSessionStatus(dataSource, fallbackStatus = 'unknown') {
  // Prioridad #1: sessionStatusAfter (estado DESPUÉS del turno)
  if (dataSource?.sessionStatusAfter && typeof dataSource.sessionStatusAfter === 'string') {
    return dataSource.sessionStatusAfter;
  }
  
  // Prioridad #2: sessionStatusBefore (estado ANTES del turno)
  if (dataSource?.sessionStatusBefore && typeof dataSource.sessionStatusBefore === 'string') {
    return dataSource.sessionStatusBefore;
  }
  
  // Prioridad #3: status (campo legacy)
  if (dataSource?.status && typeof dataSource.status === 'string') {
    return dataSource.status;
  }
  
  // Prioridad #4: fallback legacy (para compatibilidad temporal)
  if (fallbackStatus && typeof fallbackStatus === 'string') {
    return fallbackStatus;
  }
  
  return 'unknown';
}

/**
 * 🎟️ REDISEÑO PRO: Mapeo pedagógico hardcoded en FE
 * Diccionario de objetivos por fase (versión PRO)
 */
const PHASE_OBJECTIVES = {
  // Fases de inicio/contexto
  'TESTEO_DE_SENORITY': 'Probar seniority y claridad',
  'CONTEXTO': 'Presentar tu propuesta inicial',
  'WARMUP': 'Presentar tu propuesta inicial',
  'APERTURA': 'Presentar tu propuesta inicial',
  'OPENING': 'Presentar tu propuesta inicial',
  'HIPOTESIS': 'Presentar tu propuesta inicial',
  
  // Fases de desarrollo/negociación
  'REENCUADRE_DE_NECESIDAD': 'Defender valor frente a objeciones',
  'BUILD': 'Defender valor frente a objeciones',
  'ANALISIS': 'Defender valor frente a objeciones',
  'NEGOCIACION': 'Defender valor frente a objeciones',
  'PRESION': 'Defender valor frente a objeciones',
  
  // Fases de evaluación/riesgo
  'IMPACTO_Y_RIESGO': 'Evaluar impacto, riesgos y tradeoffs',
  'MEASURE': 'Evaluar impacto, riesgos y tradeoffs',
  'VALIDACION': 'Evaluar impacto, riesgos y tradeoffs',
  
  // Fases de cierre/liderazgo
  'VALIDACION_DE_LIDERAZGO': 'Validar ownership y liderazgo',
  'CIERRE': 'Cerrar condiciones y compromisos',
  'DECISION': 'Cerrar condiciones y compromisos',
  'LEARN': 'Cerrar condiciones y compromisos',
  'CLOSING': 'Cerrar condiciones y compromisos'
};

/**
 * 🎟️ REDISEÑO PRO: Turnos objetivo por fase hardcoded
 * Para mostrar "A de B" en progreso de fase
 */
const PHASE_MAX_TURNS = {
  'TESTEO_DE_SENORITY': 3,
  'REENCUADRE_DE_NECESIDAD': 5,
  'IMPACTO_Y_RIESGO': 5,
  'VALIDACION_DE_LIDERAZGO': 4,
  
  // Defaults para fases comunes
  'CONTEXTO': 2,
  'BUILD': 4,
  'MEASURE': 3,
  'LEARN': 3,
  'CIERRE': 3
};

const SessionStatusBadge = ({ 
  sessionStatus, 
  dataSource, 
  moderatorAlert,
  currentPhase, 
  currentPhaseDisplayName,
  turnsInPhase,
  maxTurns,
  completedTurns,
  phaseConfig 
}) => {
  // 🎟️ V4-P0-008: Resolver estado efectivo con prioridad estricta
  const effectiveStatus = resolveSessionStatus(dataSource, sessionStatus);
  
  // 🎟️ P0 BUG FIX: Debug logging para validar cálculos
  console.log('🎟️ [SessionStatusBadge P0 BUG FIX] Datos recibidos:', {
    turnsInPhase,
    phaseConfig,
    effectiveStatus,
    phaseJustTransitioned: phaseConfig?.phaseJustTransitioned,
    aiTurnCount: dataSource?.aiTurnCount || completedTurns
  });
  
  // 🎟️ REDISEÑO PRO: Helper para obtener objetivo pedagógico
  const getPhaseObjective = (phase, phaseDisplayName) => {
    // Buscar por fase exacta
    const exactMatch = PHASE_OBJECTIVES[phase?.toUpperCase()];
    if (exactMatch) return exactMatch;
    
    // Buscar por display name
    const displayMatch = PHASE_OBJECTIVES[phaseDisplayName?.toUpperCase()];
    if (displayMatch) return displayMatch;
    
    // Buscar por coincidencia parcial
    const phaseKey = Object.keys(PHASE_OBJECTIVES).find(key => 
      phase?.toUpperCase().includes(key) || 
      phaseDisplayName?.toUpperCase().includes(key)
    );
    
    return PHASE_OBJECTIVES[phaseKey] || 'Completar la fase actual';
  };
  
  // 🎟️ REDISEÑO PRO: Helper para obtener turnos máximos de fase
  const getPhaseMaxTurns = (phase, phaseDisplayName, phaseConfig) => {
    // 🎟️ P0 BUG FIX: PRIORIDAD - Usar phaseMaxTurns del backend (fuente de verdad)
    if (phaseConfig?.phaseMaxTurns && typeof phaseConfig.phaseMaxTurns === 'number') {
      return phaseConfig.phaseMaxTurns;
    }
    
    // Fallback: buscar en diccionario local (solo si backend no envía)
    const exactMatch = PHASE_MAX_TURNS[phase?.toUpperCase()];
    if (exactMatch) return exactMatch;
    
    const displayMatch = PHASE_MAX_TURNS[phaseDisplayName?.toUpperCase()];
    if (displayMatch) return displayMatch;
    
    const phaseKey = Object.keys(PHASE_MAX_TURNS).find(key => 
      phase?.toUpperCase().includes(key) || 
      phaseDisplayName?.toUpperCase().includes(key)
    );
    
    return PHASE_MAX_TURNS[phaseKey] || turnsInPhase || 3; // Default 3 turnos
  };
  
  // 🎟️ P0 BUG FIX: Helper para calcular "turno actual" en fase (UX correcto)
  const calculateCurrentTurnInPhase = (turnsInPhase, phaseJustTransitioned, sessionStatus) => {
    // Si sesión está pausada, mostrar progreso actual sin incrementar
    if (sessionStatus === 'paused') {
      return Math.max(turnsInPhase, 1); // Mínimo 1 para evitar "0 de N"
    }
    
    // Si acabamos de transicionar (0 turnos en nueva fase), mostrar "1" como turno que va a ejecutar
    if (phaseJustTransitioned && turnsInPhase === 0) {
      return 1;
    }
    
    // Si turnsInPhase es 0 pero no es transición reciente, probablemente error - mostrar 1
    if (turnsInPhase === 0) {
      return 1;
    }
    
    // Caso normal: mostrar turnos completados + 1 (el turno que está ejecutando)
    // Pero si ya completó todos los turnos de la fase, mostrar el número exacto
    return turnsInPhase;
  };

  // 🎟️ REDISEÑO PRO: Configuración de estados (COLORES NEUTRALES)
  const statusConfig = {
    active: {
      title: 'Sesión en curso',
      icon: Play,
      iconColor: '#2563EB', // Azul neutro
      footer: 'Esperando tu respuesta'
    },
    paused: {
      title: 'Sesión pausada',
      icon: Pause,
      iconColor: '#D97706', // Ámbar suave
      footer: 'Sesión detenida temporalmente'
    },
    armed_to_close: {
      title: 'Sesión en curso',
      icon: Clock,
      iconColor: '#2563EB', // Azul neutro (como active)
      footer: 'Último turno alcanzado'
    },
    completed: {
      title: 'Sesión finalizada',
      icon: CheckCircle,
      iconColor: '#16A34A', // Verde suave
      footer: 'Sesión completada'
    },
    closed: {
      title: 'Sesión finalizada',
      icon: CheckCircle,
      iconColor: '#16A34A', // Verde suave
      footer: 'Sesión completada'
    }
  };

  // Resolver configuración
  const config = statusConfig[effectiveStatus] || statusConfig.active;
  const StatusIcon = config.icon;
  
  // 🎟️ P0 BUG FIX: Calcular valores usando fuente de verdad del backend
  const aiTurnCount = dataSource?.aiTurnCount || completedTurns || 0;
  const progressPercentage = maxTurns > 0 ? Math.round((aiTurnCount / maxTurns) * 100) : 0;
  const phaseLabel = currentPhaseDisplayName || currentPhase || 'Fase actual';
  const phaseObjective = getPhaseObjective(currentPhase, currentPhaseDisplayName);
  
  // 🎟️ P0 BUG FIX: Usar phaseMaxTurns del backend (NO inventar denominador)
  const phaseMaxTurns = getPhaseMaxTurns(currentPhase, currentPhaseDisplayName, phaseConfig);
  
  // 🎟️ P0 BUG FIX: Calcular turno actual en fase con lógica UX correcta
  const phaseJustTransitioned = phaseConfig?.phaseJustTransitioned || false;
  const currentTurnInPhase = calculateCurrentTurnInPhase(turnsInPhase, phaseJustTransitioned, effectiveStatus);

  return (
    <div 
      className="border rounded-2xl p-4 shadow-sm"
      style={{
        backgroundColor: '#F9FAFB',
        borderColor: '#E5E7EB'
      }}
    >
      {/* 🎟️ REDISEÑO PRO: Header (fila 1) */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <StatusIcon 
            className="w-4 h-4" 
            style={{ color: config.iconColor }}
          />
          <span className="text-base font-semibold" style={{ color: '#111827' }}>
            {config.title}
          </span>
        </div>
        
        {/* Badge pill turnos */}
        <div 
          className="px-3 py-1 rounded-full text-sm font-medium"
          style={{ 
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            color: '#111827'
          }}
        >
          {aiTurnCount} / {maxTurns} turnos
        </div>
      </div>

      {/* 🎟️ REDISEÑO PRO: Bloque fase (fila 2) */}
      <div className="mb-3">
        <div className="text-xs font-medium mb-2" style={{ color: '#6B7280' }}>
          Fase actual
        </div>
        
        {/* Chip de fase (NO input) */}
        <div 
          className="px-3 py-2 rounded-xl border"
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: '#E5E7EB'
          }}
        >
          <div className="text-sm font-semibold mb-1" style={{ color: '#111827' }}>
            {phaseLabel}
          </div>
          <div className="text-xs" style={{ color: '#6B7280' }}>
            Objetivo: {phaseObjective}
          </div>
        </div>
      </div>

      {/* 🎟️ REDISEÑO PRO: Progreso fase (fila 3) - P0 BUG FIX */}
      <div className="mb-3">
        <div className="text-sm mb-2" style={{ color: '#111827' }}>
          <span className="font-medium">Turno en esta fase:</span>{' '}
          <span className="font-semibold">{currentTurnInPhase} de {phaseMaxTurns}</span>
        </div>
        
        {/* Barra progreso fase - P0 BUG FIX: usar currentTurnInPhase para cálculo */}
        <div 
          className="w-full h-1.5 rounded-full"
          style={{ backgroundColor: '#E5E7EB' }}
        >
          <div 
            className="h-1.5 rounded-full transition-all duration-300"
            style={{ 
              backgroundColor: config.iconColor,
              width: `${phaseMaxTurns > 0 ? Math.min((currentTurnInPhase / phaseMaxTurns) * 100, 100) : 0}%`
            }}
          />
        </div>
      </div>

      {/* 🎟️ REDISEÑO PRO: Progreso total (fila 4) */}
      <div className="mb-3">
        <div className="flex justify-between items-center text-sm mb-2">
          <span className="font-medium" style={{ color: '#111827' }}>
            Avance total
          </span>
          <span className="font-semibold" style={{ color: '#111827' }}>
            {progressPercentage}%
          </span>
        </div>
        
        {/* Barra progreso total */}
        <div 
          className="w-full h-1.5 rounded-full"
          style={{ backgroundColor: '#E5E7EB' }}
        >
          <div 
            className="h-1.5 rounded-full transition-all duration-300"
            style={{ 
              backgroundColor: config.iconColor,
              width: `${progressPercentage}%`
            }}
          />
        </div>
      </div>

      {/* 🎟️ REDISEÑO PRO: Footer (fila 5) */}
      <div className="text-sm" style={{ color: '#6B7280' }}>
        {config.footer}
      </div>
    </div>
  );
};

export default SessionStatusBadge;